import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import {
    RefreshCw, Send, Bot, Users, Hash, CheckCircle2, XCircle, Plug, Save, Trash2, Plus, Rocket,
} from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import DiscordHealthCard from "@/components/DiscordHealthCard";
import ConfirmButton from "@/components/common/ConfirmButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { timeAgo } from "@/lib/communities";

const TABS = ["Health", "Role Mapping", "Channel Mapping", "Clan Roles", "Slash Commands", "Sync Logs", "Setup Wizard"];

export default function Discord() {
    const [tab, setTab] = useState("Health");
    const { data: status, reload: reloadStatus } = useFetch("/discord/status");

    return (
        <div data-testid="admin-discord">
            <PageHeader title="Discord Integration" subtitle="Connect, map, and synchronize your Discord guild." />
            <div className="flex flex-wrap gap-1 mb-5 border-b border-border">
                {TABS.map((t) => (
                    <button key={t} data-testid={`discord-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setTab(t)}
                        className={`px-4 py-2 font-heading uppercase tracking-wide text-sm border-b-2 transition-colors ${
                            tab === t ? "border-burnt text-burnt" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {t}
                    </button>
                ))}
            </div>

            {tab === "Health" && <Health status={status} reload={reloadStatus} />}
            {tab === "Role Mapping" && <RoleMapping />}
            {tab === "Channel Mapping" && <ChannelMapping />}
            {tab === "Clan Roles" && <ClanRoles />}
            {tab === "Slash Commands" && <SlashCommands status={status} />}
            {tab === "Sync Logs" && <SyncLogs />}
            {tab === "Setup Wizard" && <SetupWizard status={status} />}
            <Footer />
        </div>
    );
}

function Health({ status, reload }) {
    const [busy, setBusy] = useState("");
    if (!status) return <Loader />;

    const action = async (a) => {
        setBusy(a);
        try {
            if (a === "roles") {
                const { data } = await api.post("/discord/auto-map-roles");
                toast.success(`Synced ${data.matched.length} roles from your server`,
                    { description: data.unmatched.length ? `Not found in guild: ${data.unmatched.join(", ")}` : "All role mappings updated with real IDs." });
            } else if (a === "channels") {
                const { data } = await api.post("/discord/auto-map-channels");
                toast.success(`Synced ${data.matched.length} channels from your server`,
                    { description: data.unmatched.length ? `Not found in guild: ${data.unmatched.join(", ")}` : "All channel mappings updated with real IDs." });
            } else {
                const { data } = await api.post("/discord/sync", { action: a });
                if (data.success) toast.success(`${a.replace(/_/g, " ")} complete`);
                else toast.error("Action failed", { description: "Bot is offline or not in the server yet." });
            }
            reload();
        } catch (e) {
            toast.error("Action failed", { description: e.response?.data?.detail || "" });
        } finally { setBusy(""); }
    };

    const buttons = [
        { a: "reconnect", label: "Reconnect", icon: Plug },
        { a: "test_bot", label: "Test Bot", icon: Bot },
        { a: "guild", label: "Refresh Guild", icon: RefreshCw },
        { a: "members", label: "Sync Members", icon: Users },
        { a: "roles", label: "Sync Roles", icon: RefreshCw },
        { a: "channels", label: "Sync Channels", icon: Hash },
        { a: "test_announcement", label: "Test Announcement", icon: Send },
    ];

    return (
        <div className="space-y-5">
            <DiscordHealthCard status={status} />
            <SectionCard title="Bot Controls">
                <div className="flex flex-wrap gap-2">
                    {buttons.map((b) => (
                        <Button key={b.a} data-testid={`discord-action-${b.a}`} disabled={busy === b.a} variant="outline"
                            onClick={() => action(b.a)}
                            className="font-heading uppercase border-border hover:border-burnt/50 hover:text-burnt">
                            <b.icon size={14} className="mr-1.5" /> {b.label}
                        </Button>
                    ))}
                </div>
            </SectionCard>
            <SectionCard title="Guild Diagnostics">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <Diag label="Guild ID" value={status.guild_id} mono />
                    <Diag label="Bot User ID" value={status.bot_user_id} mono />
                    <Diag label="Failed Syncs" value={status.failed_syncs} />
                    <Diag label="Member Count" value={(status.member_count || 0).toLocaleString()} />
                </div>
                <div className="mt-4">
                    <div className="label-mono text-muted-foreground mb-2">Bot Permissions</div>
                    <div className="flex flex-wrap gap-2">
                        {(status.bot_permissions || []).map((p) => (
                            <span key={p} className="rounded-sm border border-radioactive/30 bg-radioactive/10 px-2 py-0.5 label-mono text-radioactive">{p}</span>
                        ))}
                    </div>
                </div>
            </SectionCard>
        </div>
    );
}

function Diag({ label, value, mono }) {
    return (
        <div className="rounded-md border border-border bg-background/40 p-3">
            <div className="label-mono text-muted-foreground">{label}</div>
            <div className={`text-foreground mt-1 ${mono ? "font-mono text-xs break-all" : "font-heading font-bold"}`}>{value ?? "—"}</div>
        </div>
    );
}

function RoleMapping() {
    const { data, reload } = useFetch("/discord/role-mappings");
    const [edits, setEdits] = useState({});
    if (!data) return <Loader />;

    const save = async (m) => {
        const patch = edits[m.id] || {};
        try {
            await api.post("/discord/role-mappings", { ...m, ...patch });
            toast.success("Mapping saved", { description: m.outpost_role });
            reload();
        } catch { toast.error("Save failed"); }
    };
    const addNew = async () => {
        await api.post("/discord/role-mappings", {
            outpost_role: data.outpost_roles[0], discord_role_name: "New Role", discord_role_id: "0", category: "network",
        });
        reload();
    };

    const CAT = { network: "Network Roles", community: "Community Roles", platform: "Platform Roles" };
    const groups = { network: [], community: [], platform: [] };
    data.mappings.forEach((m) => (groups[m.category] || groups.network).push(m));

    return (
        <div className="space-y-5">
            <div className="flex justify-end">
                <Button data-testid="add-role-mapping" onClick={addNew} className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90">
                    <Plus size={14} className="mr-1" /> Add Mapping
                </Button>
            </div>
            {Object.entries(CAT).map(([cat, label]) => (
                <SectionCard key={cat} title={label} testId={`role-group-${cat}`}>
                    <div className="space-y-2">
                        {groups[cat].map((m) => (
                            <div key={m.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center rounded-md border border-border bg-background/40 p-3">
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Outpost Role</div>
                                    <div className="font-heading font-bold text-foreground">{m.outpost_role}</div>
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Discord Role</div>
                                    <Input data-testid={`role-name-${m.id}`} defaultValue={m.discord_role_name}
                                        onChange={(e) => setEdits({ ...edits, [m.id]: { ...edits[m.id], discord_role_name: e.target.value } })}
                                        className="bg-background border-border h-8" />
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Discord Role ID</div>
                                    <Input data-testid={`role-id-${m.id}`} defaultValue={m.discord_role_id}
                                        onChange={(e) => setEdits({ ...edits, [m.id]: { ...edits[m.id], discord_role_id: e.target.value } })}
                                        className="bg-background border-border h-8 font-mono text-xs" />
                                </div>
                                <div className="flex gap-1">
                                    <Button data-testid={`save-role-${m.id}`} size="icon" className="bg-radioactive/90 text-black hover:bg-radioactive h-8 w-8" onClick={() => save(m)}>
                                        <Save size={14} />
                                    </Button>
                                    <Button size="icon" variant="outline" className="border-danger/40 text-danger hover:bg-danger/10 h-8 w-8"
                                        onClick={async () => { await api.delete(`/discord/role-mappings/${m.id}`); reload(); }}>
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            ))}
        </div>
    );
}

function ChannelMapping() {
    const { data, reload } = useFetch("/discord/channel-mappings");
    const [edits, setEdits] = useState({});
    const grouped = useMemo(() => {
        const g = {};
        (data || []).forEach((m) => { (g[m.category] = g[m.category] || []).push(m); });
        return g;
    }, [data]);
    if (!data) return <Loader />;

    const save = async (m) => {
        try {
            await api.post("/discord/channel-mappings", { ...m, ...(edits[m.id] || {}) });
            toast.success("Channel mapping saved", { description: m.function });
            reload();
        } catch { toast.error("Save failed"); }
    };

    return (
        <div className="space-y-5">
            {Object.keys(grouped).map((cat) => (
                <SectionCard key={cat} title={cat} testId={`channel-group-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="space-y-2">
                        {grouped[cat].map((m) => (
                            <div key={m.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center rounded-md border border-border bg-background/40 p-3">
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Function</div>
                                    <div className="font-heading font-bold text-foreground">{m.function}</div>
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Channel</div>
                                    <Input data-testid={`channel-name-${m.id}`} defaultValue={m.channel_name}
                                        onChange={(e) => setEdits({ ...edits, [m.id]: { ...edits[m.id], channel_name: e.target.value } })}
                                        className="bg-background border-border h-8" />
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Channel ID</div>
                                    <Input data-testid={`channel-id-${m.id}`} defaultValue={m.channel_id}
                                        onChange={(e) => setEdits({ ...edits, [m.id]: { ...edits[m.id], channel_id: e.target.value } })}
                                        className="bg-background border-border h-8 font-mono text-xs" />
                                </div>
                                <Button data-testid={`save-channel-${m.id}`} size="icon" className="bg-radioactive/90 text-black hover:bg-radioactive h-8 w-8" onClick={() => save(m)}>
                                    <Save size={14} />
                                </Button>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            ))}
        </div>
    );
}

function ClanRoles() {
    const { data: clans, reload } = useFetch("/clans");
    const { data: communities } = useFetch("/communities");
    const [edits, setEdits] = useState({});
    if (!clans || !communities) return <Loader />;
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k;
    const save = async (c) => {
        const patch = edits[c.id] || {};
        try {
            await api.post(`/admin/clans/${c.id}/discord-role`, {
                discord_role_id: patch.discord_role_id ?? c.discord_role_id ?? "",
                discord_role_name: patch.discord_role_name ?? c.discord_role_name ?? "",
            });
            toast.success("Clan role mapped", { description: c.name });
            reload();
        } catch { toast.error("Save failed"); }
    };
    return (
        <SectionCard title="Clan → Discord Roles" testId="discord-clan-roles">
            <p className="text-sm text-muted-foreground mb-4">
                Map each clan to a Discord role ID. When auto role sync is on, members are granted the role automatically the moment they join the clan.
            </p>
            <div className="space-y-2">
                {clans.length === 0 && <div className="text-sm text-muted-foreground">No clans created yet.</div>}
                {clans.map((c) => (
                    <div key={c.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center rounded-md border border-border bg-background/40 p-3">
                        <div>
                            <div className="label-mono text-muted-foreground mb-1">Clan</div>
                            <div className="font-heading font-bold text-foreground">[{c.tag}] {c.name}</div>
                            <div className="label-mono text-muted-foreground">{nameFor(c.community_key)}</div>
                        </div>
                        <div>
                            <div className="label-mono text-muted-foreground mb-1">Discord Role Name</div>
                            <Input data-testid={`clan-role-name-${c.id}`} defaultValue={c.discord_role_name || ""}
                                onChange={(e) => setEdits({ ...edits, [c.id]: { ...edits[c.id], discord_role_name: e.target.value } })}
                                className="bg-background border-border h-8" placeholder="🎖 Clan Role" />
                        </div>
                        <div>
                            <div className="label-mono text-muted-foreground mb-1">Discord Role ID</div>
                            <Input data-testid={`clan-role-id-${c.id}`} defaultValue={c.discord_role_id || ""}
                                onChange={(e) => setEdits({ ...edits, [c.id]: { ...edits[c.id], discord_role_id: e.target.value } })}
                                className="bg-background border-border h-8 font-mono text-xs" placeholder="role id" />
                        </div>
                        <Button data-testid={`clan-role-save-${c.id}`} size="icon" className="bg-radioactive/90 text-black hover:bg-radioactive h-8 w-8" onClick={() => save(c)}>
                            <Save size={14} />
                        </Button>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

function SyncLogs() {
    const { data, reload } = useFetch("/discord/sync-logs");
    if (!data) return <Loader />;
    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <ConfirmButton testId="clear-sync-logs" confirmTestId="clear-sync-confirm"
                    onConfirm={async () => { await api.delete("/discord/sync-logs"); toast.success("Sync logs cleared"); reload(); }}
                    variant="outline" className="border-danger/40 text-danger hover:bg-danger/10 font-heading uppercase"
                    title="Clear all sync logs?" description="This permanently deletes every Discord sync log entry. This cannot be undone." confirmLabel="Clear Logs">
                    <Trash2 size={14} className="mr-1" /> Clear Logs
                </ConfirmButton>
            </div>
            <SectionCard title="Discord Sync Logs" testId="discord-sync-logs">
                <div className="space-y-2">
                    {data.length === 0 && <div className="text-sm text-muted-foreground">No sync logs. Activity will appear here.</div>}
                    {data.map((l) => (
                        <div key={l.id} data-testid={`sync-log-${l.id}`} className="flex items-start gap-3 rounded-md border border-border bg-background/40 p-3">
                            {l.status === "success"
                                ? <CheckCircle2 size={16} className="text-radioactive mt-0.5" />
                                : <XCircle size={16} className="text-danger mt-0.5" />}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-heading font-semibold uppercase text-foreground">{l.type}</span>
                                    {l.player && <span className="label-mono text-muted-foreground">{l.player}</span>}
                                    <StatusBadge status={l.status} />
                                </div>
                                <p className="text-sm text-muted-foreground">{l.detail}</p>
                                <span className="label-mono text-muted-foreground">{timeAgo(l.created_at)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </SectionCard>
        </div>
    );
}

function SlashCommands({ status }) {
    const { data: guide } = useFetch("/discord/commands-guide");
    const [busy, setBusy] = useState("");
    const disabled = status?.demo_mode || !status?.guild_joined;
    const register = async (scope) => {
        setBusy(scope);
        try {
            const { data } = await api.post(`/discord/register-commands?scope=${scope}`);
            toast.success(`Registered ${data.registered} commands`, { description: data.note });
        } catch (e) {
            toast.error("Registration failed", { description: e.response?.data?.detail || "Ensure the bot is in your server." });
        } finally { setBusy(""); }
    };
    return (
        <div className="space-y-5">
            <SectionCard title="Register Slash Commands" testId="discord-register-commands">
                <p className="text-sm text-muted-foreground mb-4">
                    Push the Outpost slash commands to Discord. <span className="text-foreground font-semibold">This Server</span> is instant;
                    <span className="text-foreground font-semibold"> Global</span> can take up to an hour to appear. The bot must be in your server first.
                </p>
                <div className="flex flex-wrap gap-2">
                    <Button data-testid="register-guild" disabled={busy === "guild" || disabled} onClick={() => register("guild")}
                        className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90">
                        <Rocket size={14} className="mr-1.5" /> Register To This Server (Instant)
                    </Button>
                    <Button data-testid="register-global" disabled={busy === "global" || disabled} variant="outline" onClick={() => register("global")}
                        className="font-heading uppercase border-border hover:border-burnt/50 hover:text-burnt">
                        <Bot size={14} className="mr-1.5" /> Register Globally
                    </Button>
                </div>
                {disabled && (
                    <p className="text-xs text-warning mt-3">
                        {status?.demo_mode
                            ? "Discord is in demo mode — add credentials to enable registration."
                            : "The bot isn't in your server yet. Invite it from the Health tab, then register."}
                    </p>
                )}
            </SectionCard>
            {guide && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <SectionCard title="Player Commands" testId="guide-player">
                        <div className="space-y-1.5">
                            {guide.player.map((c) => (
                                <div key={c.cmd} className="flex gap-3 rounded-md border border-border bg-background/40 p-2.5">
                                    <code className="font-mono text-sm text-burnt whitespace-nowrap">{c.cmd}</code>
                                    <span className="text-sm text-muted-foreground">{c.desc}</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                    <SectionCard title="Admin Commands" testId="guide-admin">
                        <div className="space-y-1.5">
                            {guide.admin.map((c) => (
                                <div key={c.cmd} className="flex gap-3 rounded-md border border-border bg-background/40 p-2.5">
                                    <code className="font-mono text-sm text-radioactive whitespace-nowrap">{c.cmd}</code>
                                    <span className="text-sm text-muted-foreground">{c.desc}</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </div>
            )}
        </div>
    );
}

const WIZARD_STEPS = [
    "Configure Discord application", "Connect bot", "Select guild", "Test permissions",
    "Map network roles", "Map community roles", "Map channels", "Test announcement", "Enable synchronization",
];

function SetupWizard({ status }) {
    const [step, setStep] = useState(0);
    const pct = Math.round(((step + 1) / WIZARD_STEPS.length) * 100);
    return (
        <SectionCard title="Discord Setup Wizard" testId="discord-setup-wizard">
            <div className="mb-4">
                <div className="flex justify-between label-mono text-muted-foreground mb-1">
                    <span>Step {step + 1} of {WIZARD_STEPS.length}</span><span>{pct}% complete</span>
                </div>
                <div className="h-2 rounded-full bg-background overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-hazard to-burnt transition-all" style={{ width: `${pct}%` }} />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5">
                <ol className="space-y-1">
                    {WIZARD_STEPS.map((s, i) => (
                        <li key={s} data-testid={`wizard-step-${i}`}
                            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm cursor-pointer ${i === step ? "bg-burnt/15 text-burnt" : i < step ? "text-radioactive" : "text-muted-foreground"}`}
                            onClick={() => setStep(i)}>
                            <span className={`flex h-5 w-5 items-center justify-center rounded-full border text-xs ${i <= step ? "border-current" : "border-border"}`}>
                                {i < step ? "✓" : i + 1}
                            </span>
                            {s}
                        </li>
                    ))}
                </ol>
                <div className="rounded-md border border-border bg-background/40 p-5">
                    <h4 className="font-heading text-xl font-bold uppercase text-foreground mb-2">{WIZARD_STEPS[step]}</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                        {status?.demo_mode
                            ? "Running in DEMO mode. Add DISCORD_CLIENT_ID, DISCORD_CLIENT_SECRET, DISCORD_BOT_TOKEN and DISCORD_GUILD_ID to the backend environment, then re-run this wizard to connect your live guild."
                            : "Credentials detected. Follow each step to verify your live Discord connection."}
                    </p>
                    <div className="flex gap-2">
                        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} className="font-heading uppercase">Back</Button>
                        {step < WIZARD_STEPS.length - 1 ? (
                            <Button data-testid="wizard-next" onClick={() => setStep(step + 1)} className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90">Next</Button>
                        ) : (
                            <Button data-testid="wizard-finish" onClick={() => toast.success("Setup complete", { description: "Synchronization enabled." })}
                                className="font-heading uppercase bg-radioactive/90 text-black hover:bg-radioactive">
                                <Rocket size={14} className="mr-1" /> Finish
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </SectionCard>
    );
}
